import { createStore } from "vuex";
import axios from "axios";

import { debounce } from "lodash-es";

import {
  adminProgramBaseUrl,
  programBaseUrl,
  programDataUrl,
  programUpdateUrl,
  userBaseUrl,
  programMemberUrls,
  programUsersCreateUrl,
  programUsersUpdateUrl,
  programMembersUnpaginatedSearchUrl,
} from "../urls.js";

import { DEFAULT_MAX_COMPETENCIES } from "../utils/constants.js";

import { userModule } from "./user.js";
import { navigationModule } from "./navigation.js";
import { programModule } from "./program.js";
import { competencyModule } from "./competency.js";
import { competencyDetailModule } from "@/programs/stores/competencyDetail.js";
import { levelUpRequestModule } from "./levelUpRequest.js";
import { opportunityDetailModule } from "./opportunityDetail.js";
import { opportunityModule } from "./opportunity.js";
import { opportunityToolbarModule } from "./opportunityToolbar.js";
import { tailoringModule } from "./tailoring.js";
import { levelUpRequestsModule } from "./levelUpRequests.js";
import { levelUpRequestDetailsModule } from "./levelUpRequestDetails.js";
import { myReflectionModule } from "./myReflection.js";
import { cohortModule } from "./cohort.js";
import { unlockModule } from "./unlock.js";

import { genericMixin } from "./generic.js";

const DEBOUNCE_MILLIS = 300;

const csrftoken = document.head.querySelector("[name='csrf-token']").attributes
  .content.value;

axios.defaults.headers.common["X-CSRF-Token"] = csrftoken;

const programInfo =
  programBaseUrl.match(location.pathname) ||
  adminProgramBaseUrl.match(location.pathname);

const initialState = {
  programId: programInfo ? programInfo.programId : null,
  programList: [],
  programData: {
    experience_configuration: {},
  },
  programMembers: [],
  programUserData: {},
  programWelcome: "",
  notification: {
    text: "",
    status: "success",
  },
};

export const store = createStore({
  modules: {
    user: userModule,
    navigation: navigationModule,
    program: programModule,
    competency: competencyModule,
    competencyDetail: competencyDetailModule,
    levelUpRequest: levelUpRequestModule,
    opportunityDetail: opportunityDetailModule,
    opportunity: opportunityModule,
    opportunityToolbar: opportunityToolbarModule,
    tailoring: tailoringModule,
    levelUpRequests: levelUpRequestsModule,
    levelUpRequestDetails: levelUpRequestDetailsModule,
    myReflection: myReflectionModule,
    cohort: cohortModule,
    unlock: unlockModule,
  },
  state: {
    ...initialState,
  },
  getters: {
    ...genericMixin.getters,
    programTitle: (state) => {
      return state.programData.name;
    },
    programConfig: (state) => {
      return state.programData.experience_configuration || {};
    },
    programDimensionsEnabled: (state, getters) => {
      // Currently all programs must have dimensions "enabled" since the
      // level-up process is based on advancement at the dimension tier
      // It's possible though for a program to have dimensions "enabled"
      // but really only using competencies (as far as the UI is concerned)
      return getters.programConfig?.has_competency_dimensions || false;
    },
    programOnboardingLevelUpEnabled: (state, getters) => {
      return getters.programConfig
        ? getters.programConfig.initial_levelset
        : false;
    },
    programIconUrl: (state, getters) => {
      return getters.programConfig ? getters.programConfig.icon_url || "" : "";
    },
    programBackgroundUrl: (state, getters) => {
      return getters.programConfig
        ? getters.programConfig.background_url || ""
        : "";
    },
    programDashboardLogoUrl: (state, getters) => {
      return getters.programConfig
        ? getters.programConfig.dashboard_logo_url || ""
        : "";
    },
    programWelcomeHtml: (state) => {
      return state.programData.dashboard_message || state.programWelcome || "";
    },
    programMaxCompetencies: (state) => {
      return (
        state.programData.experience_configuration?.active_competencies_max ||
        DEFAULT_MAX_COMPETENCIES
      );
    },
    userId: () => {
      let pageInfo = userBaseUrl.match(location.pathname);
      return pageInfo ? pageInfo.userId : null;
    },
    experienceConfiguration: (state) => {
      return state.programData.experience_configuration || {};
    },
    aboutPageText: (state, getters) => {
      return getters.experienceConfiguration.about_page_text || "";
    },
    totalStudentCount: (state) => {
      return state.programData.active_student_count;
    },
    members(state) {
      const { data = [] } = state.programMembers;
      return data.map((member) => ({
        ...member,
        url: "",
      }));
    },
    membersPagination(state) {
      return state.programMembers.meta || {};
    },
    eligibleCohortReviewers(state) {
      const ELLIGIBLE_ROLES = ["gsi", "professor"];
      return state.programMembers.filter((member) =>
        ELLIGIBLE_ROLES.includes(member.role),
      );
    },
    eligibleCohortMembers(state) {
      const ELLIGIBLE_ROLES = ["student", "observer"];
      return state.programMembers.filter((member) =>
        ELLIGIBLE_ROLES.includes(member.role),
      );
    },
    allowMembersToSetInitialLevel(state, getters) {
      return getters.experienceConfiguration.initial_levelset;
    },
    hasBadges(state) {
      return state.programData.id
        ? state.programData.experience_configuration.has_badges
        : false;
    },
    hasCareers(state) {
      return state.programData.id
        ? state.programData.experience_configuration.has_careers
        : false;
    },
    hasLevelRequests(state) {
      return state.programData.id
        ? state.programData.experience_configuration.has_level_requests
        : false;
    },
  },
  mutations: {
    ...genericMixin.mutations,
    setProgramData(state, { programData }) {
      state.programData = programData;
    },
    setUserData(state, { userData }) {
      state.programUserData = userData;
    },
    setMemberList(state, { data }) {
      state.programMembers = data;
    },
    addNewProgram(state, newProgramData) {
      state.programList.push(newProgramData);
    },
    updateProgramList(state, activeProgramData) {
      state.programList = state.programList.map((existingProgram) => {
        return existingProgram.id === activeProgramData.id
          ? activeProgramData
          : existingProgram;
      });
    },
    setNotification(state, { text, status = "success" }) {
      state.notification.text = text;
      state.notification.status = status;
    },
    clearNotification(state) {
      state.notification.text = "";
      state.notification.status = "success";
    },
  },
  actions: {
    ...genericMixin.actions,
    fetchProgramData(context, { programId }) {
      let url = programDataUrl.stringify({ programId });
      return axios.get(url);
    },
    loadProgramData({ state, commit, dispatch }) {
      let programId = state.programId;
      return dispatch("fetchProgramData", { programId })
        .then((response) => {
          commit("setProgramData", { programData: response.data });
        })
        .catch((e) => {
          console.error(e);
        });
    },
    deleteProgram(context, programData) {
      let url = programUpdateUrl.stringify({ programId: programData.id });
      return axios.delete(url, programData).then((response) => response.data);
    },
    loadMembersList: debounce(function ({ dispatch }, params) {
      return dispatch("loadObjectList", {
        urls: programMemberUrls,
        mutation: "setMemberList",
        params,
      });
    }, DEBOUNCE_MILLIS),
    async loadUnpaginatedMembersList({ getters, commit }, params) {
      let url = programMembersUnpaginatedSearchUrl.stringify({
        programId: getters.programId,
      });
      return await axios.get(url, { params }).then((members) => {
        commit("setMemberList", members);
      });
    },
    fetchUserData({ state }, { userId }) {
      return axios.get(
        programUsersUpdateUrl.stringify({
          programId: state.programId,
          userId: userId,
        }),
      );
    },
    loadUserData({ getters, commit, dispatch }) {
      let userId = getters.userId;
      return dispatch("fetchUserData", { userId }).then((response) => {
        commit("setUserData", { userData: response.data });
      });
    },
    saveUser({ dispatch }, data) {
      return dispatch(data.id ? "updateUser" : "createUser", data);
    },
    createUser({ state }, data) {
      let url = programUsersCreateUrl.stringify({ programId: state.programId });
      return axios.post(url, data).then((response) => response.data);
    },
    updateUser({ state, getters }, data) {
      let url = programUsersUpdateUrl.stringify({
        programId: state.programData.id,
        userId: getters.userId || data.id,
      });
      return axios.put(url, data).then((response) => response.data);
    },
    deleteUser({ state, getters }, data) {
      let url = programUsersUpdateUrl.stringify({
        programId: state.programData.id,
        userId: getters.userId || data.id,
      });
      return axios.delete(url, data).then((response) => response.data);
    },
    errorNotification({ commit }, text) {
      commit("setNotification", { text, status: "error" });
    },
    successNotification({ commit }, text) {
      commit("setNotification", { text });
    },
  },
});
